<template>
	<div class="mob-area-chart" ref="refChat"></div>
</template>

<script setup>
import { ref, defineExpose, getCurrentInstance, watch, nextTick } from 'vue';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ToolboxComponent, TooltipComponent, GridComponent, LegendComponent, TitleComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import dayjs from 'dayjs';
import i18n, { ZH, EN } from '@/locales';
import { useChartResize } from '../useChartResize';
import { useCommonData } from '@/hooks';
import { transformColor } from '@/utils/echarts';

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	LegendComponent,
	LineChart,
	CanvasRenderer,
	UniversalTransition,
	TitleComponent
]);

const props = defineProps({
	data: {
		type: Array,
		default: () => []
	},
	chartColor: {
		type: [String, null],
		default: null
	}
});

const { proxy } = getCurrentInstance();

const upColor = i18n.locale === ZH ? '#fa4646' : '#30bf54';
const downColor = i18n.locale === ZH ? '#30bf54' : '#fa4646';

/**
 * @param {string} txt 文案
 * @param {string} value 值
 * @param {number} comParePrice 比较的值，判断颜色
 * @param {number} prePrice 前一天收盘价
 */
function tooltipStr(txt, value, comParePrice, prePrice) {
	const color = comParePrice >= prePrice ? upColor : downColor;
	return `${txt}：<span style="color:${color}">${value}</span><br/>`;
}

// 正数补全+号
function addPlus(num) {
	return num > 0 ? '+' + num : num;
}

function splitData(rawData) {
	let categoryData = [];
	let values = [];
	for (let i = 0; i < rawData.length; i++) {
		const {
			tradingDay,
			avgPrice = 0,
			openPrice,
			closePrice,
			highPrice,
			lowPrice,
			changePct,
			changeQuota
		} = rawData[i];
		categoryData.push(dayjs(tradingDay).format('YYYY/MM/DD'));
		values.push({
			value: avgPrice,
			openPrice,
			closePrice,
			lowPrice,
			highPrice,
			changePct,
			changeQuota
		});
	}
	return {
		categoryData: categoryData,
		values
	};
}

const { getUpperThemeColor } = useCommonData();

let chatInstance = null;
const refChat = ref();

const { startListening, stopListening } = useChartResize(() => chatInstance);

const renderChart = () => {
	// 停止旧的监听
	stopListening();

	if (chatInstance) {
		chatInstance.dispose();
		chatInstance.clear();
		chatInstance = null;
	}

	if (!props.data || !props.data.length) return;

	// 确保数据按时间正序排列
	const rawData = [...props.data];
	const sortedData = rawData.reverse();
	const { categoryData, values } = splitData(sortedData);

	// 预先计算每两个月应该显示标签的索引位置（在每月的第一条数据）
	const monthLabelIndices = new Set();
	let lastMonth = -1;
	let monthCount = 0; // 月份变化计数器
	categoryData.forEach((dateStr, index) => {
		const date = dayjs(dateStr);
		const month = date.month();
		if (month !== lastMonth) {
			// 每两个月显示一次（偶数次月份变化时显示）
			if (monthCount % 2 === 0) {
				monthLabelIndices.add(index);
			}
			lastMonth = month;
			monthCount++;
		}
	});

	chatInstance = echarts.init(refChat.value);

	const themeColor = props.chartColor || getUpperThemeColor.value;

	// 指定图表的配置项和数据
	const option = {
		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'line',
				axis: 'x'
			},
			triggerOn: 'click',
			borderWidth: 1,
			borderColor: '#ccc',
			padding: 10,
			textStyle: {
				color: '#000'
			},
			position: function (pos, params, el, elRect, size) {
				const obj = {
					top: 10
				};
				obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
				return obj;
			},
			formatter: function (params) {
				let res = `<div style="color:#999;font-size:12px"><span style="color:#333">${params[0].name}</span> <br/>`;
				for (let i = 0, l = params.length; i < l; i++) {
					// 获取前一天收盘价
					const preCloseIndex = params[i].dataIndex > 0 ? params[i].dataIndex - 1 : 0;
					const preClose = values[preCloseIndex]?.closePrice || 0;

					let { data, seriesType } = params[i];

					if (seriesType === 'line') {
						res += tooltipStr(
							proxy.$t('enterprise.k-opening'),
							data['openPrice']?.toFixed(2),
							data['openPrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.k-high'),
							data['highPrice']?.toFixed(2),
							data['highPrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.k-low'),
							data['lowPrice']?.toFixed(2),
							data['lowPrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.k-closing'),
							data['closePrice']?.toFixed(2),
							data['closePrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.%Chg'),
							`${addPlus(data['changePct']?.toFixed(2))}%`,
							data['closePrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.chg'),
							data['changeQuota']?.toFixed(2),
							data['closePrice'],
							preClose
						);
					}
				}
				return res + '</div>';
			}
		},
		axisPointer: {
			label: {
				backgroundColor: '#777'
			}
		},
		grid: {
			left: '2%',
			right: '2%',
			top: '6%',
			bottom: '6%',
			containLabel: true
		},
		xAxis: {
			type: 'category',
			data: categoryData,
			boundaryGap: false, // 关闭边界间隔
			axisLine: {
				show: true,
				lineStyle: {
					color: '#F2F3F5'
				}
			},
			splitLine: {
				show: true,
				lineStyle: { color: '#F2F3F5', type: 'solid' },
				interval: index => {
					return monthLabelIndices.has(index);
				}
			},
			axisTick: {
				show: false
			},
			axisLabel: {
				show: true,
				color: '#4E5969',
				interval: 0,
				formatter: function (value, index) {
					// 隐藏第一个日期刻度
					if (index === 0) {
						return '';
					}
					// 如果当前索引在预计算的月份标签索引集合中，显示月份或年份
					if (monthLabelIndices.has(index)) {
						const date = dayjs(value);
						const month = date.month();
						// 每年的第一个月（1月）显示年份，其他月份显示月份
						if (month === 0) {
							return date.format('YYYY');
						}
						// 根据语言环境格式化月份：英文使用单词简写缩写（MMM），中文使用数字+月
						if (i18n.locale === EN) {
							return date.format('MMM');
						}
						return `${month + 1}月`;
					}
					return '';
				}
			}
		},
		yAxis: {
			scale: true,
			type: 'value',
			axisLine: {
				show: true,
				lineStyle: {
					color: '#F2F3F5'
				}
			},
			axisLabel: {
				show: true,
				inside: true,
				color: '#4E5969'
			},
			splitNumber: 3,
			splitLine: {
				show: true,
				lineStyle: { color: '#F2F3F5', type: 'solid' }
			}
		},
		series: [
			{
				type: 'line',
				itemStyle: {
					normal: {
						color: themeColor,
						opacity: 0
					},
					emphasis: {
						opacity: 1
					}
				},
				areaStyle: {
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: themeColor
						},
						{
							offset: 1,
							color: transformColor(themeColor, 0.8)
						}
					])
				},
				data: values
			}
		]
	};

	// 使用刚指定的配置项和数据显示图表。
	chatInstance.setOption(option);

	chatInstance.on('mousemove', function () {
		chatInstance.dispatchAction({
			type: 'hideTip'
		});
	});

	// 开始监听 resize
	startListening();
};

watch(
	() => props.data,
	() => {
		nextTick(() => {
			renderChart();
		});
	},
	{ immediate: true, deep: true }
);

defineExpose({
	refresh: renderChart
});
</script>

<style lang="less" scoped>
.mob-area-chart {
	width: 100%;
	height: 100%;
}
</style>
